import api from './api';
import { fetchPage } from './pagedParams';

const productService = {
  // Products
  getAllProducts: async () => {
    const response = await api.get('/admin/products');
    return response.data;
  },

  getProductsPaginated: async (page = 0, size = 10, search = '') => {
    const params = new URLSearchParams({ page, size });
    if (search && search.trim()) params.append('search', search.trim());
    const response = await api.get(`/admin/products/paginated?${params.toString()}`);
    return response.data;
  },

  getActiveProducts: async () => {
    const response = await api.get('/admin/products/active');
    return response.data;
  },

  searchProducts: async (query) => {
    const response = await api.get(`/admin/products/search?query=${query}`);
    return response.data;
  },

  getProductsByCategory: async (categoryId) => {
    const response = await api.get(`/admin/products/category/${categoryId}`);
    return response.data;
  },

  getProductById: async (id) => {
    const response = await api.get(`/admin/products/${id}`);
    return response.data;
  },

  getProductBySlug: async (slug) => {
    const response = await api.get(`/admin/products/slug/${slug}`);
    return response.data;
  },

  createProduct: async (productData) => {
    const response = await api.post('/admin/products', productData);
    return response.data;
  },

  updateProduct: async (id, productData) => {
    const response = await api.put(`/admin/products/${id}`, productData);
    return response.data;
  },

  toggleProductSellOnline: async (id) => {
    const response = await api.patch(`/admin/products/${id}/toggle-sell-online`);
    return response.data;
  },

  toggleProductActive: async (id) => {
    const response = await api.patch(`/admin/products/${id}/toggle-active`);
    return response.data;
  },

  deleteProduct: async (id) => {
    await api.delete(`/admin/products/${id}`);
  },

  // Attributes
  getAllAttributes: async () => {
    const response = await api.get('/admin/attributes');
    return response.data;
  },

  getRequiredAttributes: async () => {
    const response = await api.get('/admin/attributes/required');
    return response.data;
  },

  // Attribute values not yet used by an active variant of this product —
  // powers the "create new variant" dropdown in PurchaseOrderForm so it
  // can't offer a combination (e.g. a volume) that already exists.
  getAvailableAttributeValues: async (productId, attributeId) => {
    const response = await api.get(`/admin/products/${productId}/available-attribute-values`, {
      params: { attributeId },
    });
    return response.data;
  },

  getAttributeById: async (id) => {
    const response = await api.get(`/admin/attributes/${id}`);
    return response.data;
  },

  createAttribute: async (attributeData) => {
    const response = await api.post('/admin/attributes', attributeData);
    return response.data;
  },

  updateAttribute: async (id, attributeData) => {
    const response = await api.put(`/admin/attributes/${id}`, attributeData);
    return response.data;
  },

  toggleAttributeActive: async (id) => {
    const response = await api.patch(`/admin/attributes/${id}/toggle-active`);
    return response.data;
  },

  deleteAttribute: async (id) => {
    await api.delete(`/admin/attributes/${id}`);
  },

  // Attribute Values
  getAttributeValues: async (attributeId) => {
    const response = await api.get(`/admin/attribute-values/attribute/${attributeId}`);
    return response.data;
  },

  getAttributeValueById: async (id) => {
    const response = await api.get(`/admin/attribute-values/${id}`);
    return response.data;
  },

  createAttributeValue: async (valueData) => {
    const response = await api.post('/admin/attribute-values', valueData);
    return response.data;
  },

  updateAttributeValue: async (id, valueData) => {
    const response = await api.put(`/admin/attribute-values/${id}`, valueData);
    return response.data;
  },

  deleteAttributeValue: async (id) => {
    await api.delete(`/admin/attribute-values/${id}`);
  },

  // Product Variants
  getProductVariants: async (productId) => {
    const response = await api.get(`/admin/product-variants/product/${productId}`);
    return response.data;
  },

  getActiveProductVariants: async (productId) => {
    const response = await api.get(`/admin/product-variants/product/${productId}/active`);
    return response.data;
  },

  getLowStockVariants: async (threshold = 10) => {
    const response = await api.get(`/admin/product-variants/low-stock?threshold=${threshold}`);
    return response.data;
  },

  getOutOfStockVariants: async () => {
    const response = await api.get('/admin/product-variants/out-of-stock');
    return response.data;
  },

  getProductVariantById: async (id) => {
    const response = await api.get(`/admin/product-variants/${id}`);
    return response.data;
  },

  createProductVariant: async (variantData) => {
    const response = await api.post('/admin/product-variants', variantData);
    return response.data;
  },

  updateProductVariant: async (id, variantData) => {
    const response = await api.put(`/admin/product-variants/${id}`, variantData);
    return response.data;
  },

  toggleVariantActive: async (id) => {
    const response = await api.patch(`/admin/product-variants/${id}/toggle-active`);
    return response.data;
  },

  deleteProductVariant: async (id) => {
    await api.delete(`/admin/product-variants/${id}`);
  },

  // Product Images
  uploadMainImage: async (productId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/admin/products/${productId}/image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  deleteMainImage: async (productId) => {
    await api.delete(`/admin/products/${productId}/image`);
  },

  uploadGalleryImage: async (productId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/admin/products/${productId}/gallery`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  deleteGalleryImage: async (productId, imageUrl) => {
    await api.delete(`/admin/products/${productId}/gallery`, { params: { url: imageUrl } });
  },

  // Stock Movements
  getProductStockMovements: async (productId) => {
    const response = await api.get(`/admin/stock-movements/product/${productId}`);
    return response.data;
  },

  getVariantStockMovements: async (variantId) => {
    const response = await api.get(`/admin/stock-movements/variant/${variantId}`);
    return response.data;
  },

  getStoreStockMovements: async () => {
    const response = await api.get('/admin/stock-movements/store');
    return response.data;
  },

  getStockMovementsByType: async (movementType) => {
    const response = await api.get(`/admin/stock-movements/store/type/${movementType}`);
    return response.data;
  },

  createStockMovement: async (movementData) => {
    const response = await api.post('/admin/stock-movements', movementData);
    return response.data;
  },

  adjustStock: async (adjustmentData) => {
    const response = await api.post('/admin/stock-movements/adjust', adjustmentData);
    return response.data;
  },

  // Move stock of a variant to another store (destination product/variant auto-created if missing).
  transferStock: async (transferData) => {
    const response = await api.post('/admin/stock-movements/transfer', transferData);
    return response.data;
  },

  // Whole product: every variant's available (non-reserved) stock goes to the destination store.
  transferProduct: async (transferData) => {
    const response = await api.post('/admin/stock-movements/transfer-product', transferData);
    return response.data;
  },

  // Active stores a transfer can go to (id/name/code), excluding the source store.
  getTransferDestinations: async (excludeStoreId) => {
    const response = await api.get('/admin/stock-movements/transfer-destinations', {
      params: excludeStoreId ? { excludeStoreId } : {},
    });
    return response.data;
  },

  // Price History
  getVariantPriceHistory: async (variantId) => {
    const response = await api.get(`/admin/price-history/variant/${variantId}`);
    return response.data;
  },

  getStorePriceHistory: async (startDate, endDate) => {
    const response = await api.get(`/admin/price-history/store?startDate=${startDate}&endDate=${endDate}`);
    return response.data;
  },

  updatePrice: async (priceData) => {
    const response = await api.post('/admin/price-history/update', priceData);
    return response.data;
  },

  // Server-side paged list (Spring Page) for the URL-driven DataTable.
  getProductsPage: (params) => fetchPage('/admin/products/paged', params),

  // Server-side paged list (Spring Page) for the URL-driven DataTable.
  getAttributesPage: (params) => fetchPage('/admin/attributes/paged', params),

  // Server-side paged list (Spring Page) for the URL-driven DataTable.
  getStockMovementsPage: (params) => fetchPage('/admin/stock-movements/paged', params),

};

export default productService;